import { useEffect, useState } from "react";
import {
	Badge,
	Box,
	Container,
	Divider,
	Heading,
	IconButton,
	Image,
	SimpleGrid,
	Spinner,
	Stack,
	Text,
} from "@chakra-ui/react";
import { MarketingLayout } from "../components/marketing-layout";
import { InlineContactSection } from "../components/marketing-layout/InlineContactSection";
import { HowItWorksSection } from "../components/marketing-layout/HowItWorksSection";
import { MdChevronLeft, MdChevronRight } from "react-icons/md";
import { useCmsApi } from "../services/useCmsApi";
import { ICaseStudy } from "../models/ICmsModels";

type Prescription = {
	image: string;
	label: string;
	alt: string;
};

type WeedYear = {
	year: string;
	src: string;
};

type Farm = {
	id: string;
	name: string;
	region: string;
	problem: string;
	weedHistory: WeedYear[];
	prescriptions: Prescription[];
	action: string;
	tags: string[];
};

const farms: Farm[] = [
	// Simple → complex order. Field numbers reflect original farm IDs, not display order.
	{
		id: "Case Study 1",
		name: "Field 1",
		region: "Saskatchewan, Canada",
		problem:
			"Stubborn kochia patches had persisted across a large portion of this field for five consecutive years, resisting standard broadcast treatments and continuing to spread despite repeated herbicide applications.",
		weedHistory: [
			{ year: "2020", src: "/case-studies/case3/weed_pressure_2020.png" },
			{ year: "2021", src: "/case-studies/case3/weed_pressure_2021.png" },
			{ year: "2022", src: "/case-studies/case3/weed_pressure_2022.png" },
			{ year: "2023", src: "/case-studies/case3/weed_pressure_2023.png" },
			{ year: "2024", src: "/case-studies/case3/weed_pressure_2024.png" },
		],
		prescriptions: [
			{
				image: "/case-studies/case3/Beach%20Home_Rx_option_focused.png",
				label: "Herbicide Prescription",
				alt: "Combined prescription map - blue zones show where variable herbicide rates and increased seeding rates are applied",
			},
		],
		action:
			"Geco generated a targeted Edge (ethalfluralin) residual herbicide prescription to be applied only within the predicted high risk weed zones at full rate. Grey areas of the field received no treatment during this pass – protecting yield potential and reducing unnecessary chemical use while putting herbicide exactly where it was needed.",
		tags: ["Multi-rate herbicide", "Kochia"],
	},
	{
		id: "Case Study 2",
		name: "Field 2",
		region: "Saskatchewan, Canada",
		problem:
			"Persistent kochia patches had concentrated across the field for years. The grower wanted to apply a new herbicide and till the worst-affected areas but needed to know where to target these operations.",
		weedHistory: [
			{ year: "2019", src: "/case-studies/case2/weed_pressure_2019.png" },
			{ year: "2020", src: "/case-studies/case2/weed_pressure_2020.png" },
			{ year: "2021", src: "/case-studies/case2/weed_pressure_2021.png" },
			{ year: "2022", src: "/case-studies/case2/weed_pressure_2022.png" },
			{ year: "2023", src: "/case-studies/case2/weed_pressure_2023.png" },
		],
		prescriptions: [
			{
				image: "/case-studies/case2/Fahlman%20Field%203_spray_map_options.png",
				label: "Herbicide Prescription",
				alt: "Targeted herbicide prescription - blue zones show where chemical treatment will be applied",
			},
			{
				image: "/case-studies/case2/Fahlman%20Field%203_tillage.png",
				label: "Tillage Prescription",
				alt: "Separate tillage prescription - orange zones mark areas requiring mechanical tillage in the northern portion of the field",
			},
		],
		action:
			"Geco created two prescriptions: a targeted herbicide map (blue zones) and a separate tillage map (orange zones), allowing the grower to treat only the problem areas without disturbing healthy sections of the field.",
		tags: ["Herbicide Prescription", "Tillage Prescription", "Kochia"],
	},
	{
		id: "Case Study 3",
		name: "Field 3",
		region: "Saskatchewan, Canada",
		problem:
			"Persistent weed pressure was distributed across the field over five seasons. The grower wanted to attack the problem on multiple fronts: variable herbicide rates and increased seeding density.",
		weedHistory: [
			{ year: "2019", src: "/case-studies/case1/weed_pressure_2019.png" },
			{ year: "2020", src: "/case-studies/case1/weed_pressure_2020.png" },
			{ year: "2021", src: "/case-studies/case1/weed_pressure_2021.png" },
			{ year: "2022", src: "/case-studies/case1/weed_pressure_2022.png" },
			{ year: "2023", src: "/case-studies/case1/weed_pressure_2023.png" },
		],
		prescriptions: [
			{
				image: "/case-studies/case1/Heinrich%20Out%20West_multilayer_spray_map.png",
				label: "Three-rate herbicide prescription",
				alt: "Three-rate herbicide prescription - dark blue (high-rate), light blue (standard-rate), and gray (untreated) zones",
			},
			{
				image: "/case-studies/case1/Heinrich%20Out%20West_spray_map_options.png",
				label: "Two-rate seeding prescription",
				alt: "Two-rate seeding prescription - blue zones show where seeding rate is increased to boost crop competition",
			},
		],
		action:
			"Geco delivered a prescription map for applying three different rates of herbicide to match predicted weed pressures throughout the field. Geco prescription map was also used to increase seeding rate in the worst-affected zones to boost crop competition.",
		tags: ["Multi-rate herbicide", "Variable rate seeding", "Kochia"],
	},
];

const WeedHistoryCarousel = ({ years }: { years: WeedYear[] }): JSX.Element => {
	const [index, setIndex] = useState(0);
	if (years.length === 0) {
		return <Text fontSize="sm" color="gray.400">No weed history data</Text>;
	}
	const prev = () => setIndex((i) => (i - 1 + years.length) % years.length);
	const next = () => setIndex((i) => (i + 1) % years.length);
	const current = years[index];

	return (
		<Stack spacing="2">
			<Box position="relative" borderRadius="md" overflow="hidden" border="1px solid" borderColor="gray.100" bg="gray.50">
				<Image
					src={current.src}
					alt={`Weed pressure map ${current.year}`}
					w="100%"
					objectFit="contain"
					loading="lazy"
				/>
				{/* Year label */}
				<Box position="absolute" top="2" left="2" bg="blackAlpha.600" color="white" px="2" py="0.5" borderRadius="md" fontSize="xs" fontWeight="bold">
					{current.year}
				</Box>
				{/* Nav buttons */}
				<IconButton
					aria-label="Previous year"
					icon={<MdChevronLeft size={20} />}
					position="absolute"
					left="1"
					top="50%"
					transform="translateY(-50%)"
					size="sm"
					variant="solid"
					bg="whiteAlpha.800"
					color="gray.700"
					_hover={{ bg: "white" }}
					onClick={prev}
				/>
				<IconButton
					aria-label="Next year"
					icon={<MdChevronRight size={20} />}
					position="absolute"
					right="1"
					top="50%"
					transform="translateY(-50%)"
					size="sm"
					variant="solid"
					bg="whiteAlpha.800"
					color="gray.700"
					_hover={{ bg: "white" }}
					onClick={next}
				/>
			</Box>
			{/* Dot navigation */}
			<Stack direction="row" justify="center" spacing="1.5">
				{years.map((y, i) => (
					<Box
						key={y.year}
						as="button"
						aria-label={`Go to year ${y.year}`}
						w={i === index ? "18px" : "8px"}
						h="8px"
						borderRadius="full"
						bg={i === index ? "blue.500" : "gray.300"}
						transition="all 0.2s"
						onClick={() => setIndex(i)}
					/>
				))}
			</Stack>
			<Text fontSize="xs" color="gray.400" textAlign="center">
				Green = weed presence · Brown = clean crop
			</Text>
		</Stack>
	);
};

const FarmCard = ({ farm }: { farm: Farm }): JSX.Element => (
	<Stack
		bg="white"
		borderRadius="xl"
		boxShadow="md"
		overflow="hidden"
		border="1px solid"
		borderColor="gray.100"
		spacing="0"
		transition="box-shadow 0.2s ease, transform 0.2s ease"
		_hover={{ boxShadow: { md: "xl" }, transform: { md: "translateY(-3px)" } }}
	>
		<Box bg="blue.700" px={{ base: "6", md: "8" }} py={{ base: "4", md: "6" }} minH={{ base: "80px", md: "96px" }} display="flex" alignItems="center">
			<Stack spacing="1" w="full">
				<Stack direction="row" justify="space-between" align="center">
					<Heading size={{ base: "md", md: "lg" }} color="white" whiteSpace="nowrap">{farm.name}</Heading>
					<Text fontSize={{ base: "sm", md: "md" }} color="blue.200">{farm.region}</Text>
				</Stack>
				<Text fontSize="xs" color="blue.300">{farm.id}</Text>
			</Stack>
		</Box>

		<Stack p={{ base: "6", md: "8" }} spacing={{ base: "5", md: "6" }}>
			<Stack spacing="2">
				<Text fontSize="xs" fontWeight="bold" color="gray.400" textTransform="uppercase" letterSpacing="wide">
					{farm.weedHistory.length > 0
						? `${farm.weedHistory.length}-Year Weed History (${farm.weedHistory[0].year}–${farm.weedHistory[farm.weedHistory.length - 1].year})`
						: "Weed History"}
				</Text>
				{farm.weedHistory.length === 0
					? <Text fontSize="sm" color="gray.400">No weed history data</Text>
					: <WeedHistoryCarousel years={farm.weedHistory} />}
			</Stack>

			<Divider />

			<Stack spacing="2">
				<Text fontSize="xs" fontWeight="bold" color="gray.400" textTransform="uppercase" letterSpacing="wide">
					Background
				</Text>
				<Text color="gray.600" fontSize={{ base: "sm", md: "md" }}>{farm.problem}</Text>
			</Stack>

			<Divider />

			<Stack spacing="3">
				<Text fontSize="xs" fontWeight="bold" color="gray.400" textTransform="uppercase" letterSpacing="wide">
					Geco Prescriptions{farm.weedHistory.length > 0 ? ` (${Number(farm.weedHistory[farm.weedHistory.length - 1].year) + 1})` : ""}
				</Text>
				<SimpleGrid columns={farm.prescriptions.length > 1 ? 2 : 1} spacing={{ base: "3", md: "4" }} alignItems="start">
					{farm.prescriptions.map((rx) => (
						<Stack key={rx.label} spacing="1">
							<Box
								borderRadius="md"
								overflow="hidden"
								border="1px solid"
								borderColor="blue.100"
								bg="gray.50"
								h={{ base: "180px", md: "260px" }}
								display="flex"
								alignItems="center"
								justifyContent="center"
							>
								<Image
									src={rx.image}
									alt={rx.alt}
									maxH={{ base: "180px", md: "260px" }}
									maxW="100%"
									objectFit="contain"
									loading="lazy"
								/>
							</Box>
							<Text fontSize={{ base: "xs", md: "sm" }} color="blue.600" fontWeight="semibold" textAlign="center">
								{rx.label}
							</Text>
						</Stack>
					))}
				</SimpleGrid>
				<Text color="gray.700" fontSize={{ base: "sm", md: "md" }}>{farm.action}</Text>
			</Stack>

			<Stack direction="row" flexWrap="wrap" spacing="2">
				{farm.tags.map((t) => (
					<Badge key={t} colorScheme="blue" fontSize={{ base: "xs", md: "sm" }}>{t}</Badge>
				))}
			</Stack>
		</Stack>
	</Stack>
);

const mapCaseStudyToFarm = (cs: ICaseStudy): Farm => ({
	id: `Case Study ${cs.id}`,
	name: cs.name,
	region: cs.region || "",
	problem: cs.problem || "",
	action: cs.action || "",
	tags: cs.tags ? cs.tags.split(",").map((t) => t.trim()) : [],
	weedHistory: cs.images
		.filter((img) => img.imageType === "weedHistory")
		.sort((a, b) => a.sortOrder - b.sortOrder)
		.map((img) => ({ year: img.label || "", src: img.imageUrl })),
	prescriptions: cs.images
		.filter((img) => img.imageType === "prescription")
		.sort((a, b) => a.sortOrder - b.sortOrder)
		.map((img) => ({ image: img.imageUrl, label: img.label || "", alt: img.altText || "" })),
});

export const ExampleFarmsPage = (): JSX.Element => {
	const cmsApi = useCmsApi();
	const [displayFarms, setDisplayFarms] = useState<Farm[]>(farms);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		let cancelled = false;
		cmsApi.getCaseStudies().then((caseStudies) => {
			if (!cancelled) {
				if (caseStudies.length > 0) {
					setDisplayFarms(caseStudies.map(mapCaseStudyToFarm));
				}
				setLoading(false);
			}
		}).catch(() => { if (!cancelled) setLoading(false); });
		return () => { cancelled = true; };
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<MarketingLayout>
			<>
				<Box bg="blue.700" color="white" py={{ base: "16", md: "24" }}>
					<Container maxW="1560px">
						<Stack spacing="4" maxW="2xl">
							<Heading size={{ base: "2xl", md: "3xl" }} fontWeight="extrabold">
								See It In Use
							</Heading>
							<Text fontSize="xl" opacity={0.9}>
								Real farms. Real results. See how growers are using Geco to reduce weed pressure and protect yield.
							</Text>
						</Stack>
					</Container>
				</Box>
				<HowItWorksSection />

				<Box py={{ base: "12", md: "20" }}>
					<Container maxW="1560px">
						<Stack spacing="8">
							<Heading size="lg" color="gray.800">Farm Case Studies</Heading>
							{loading && <Spinner color="green.500" />}
							<SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing="6">
								{displayFarms.map((farm) => (
									<FarmCard key={farm.id} farm={farm} />
								))}
							</SimpleGrid>
						</Stack>
					</Container>
				</Box>

				<InlineContactSection />
			</>
		</MarketingLayout>
	);
};
